import { useState } from "preact/hooks";
import { validateAndNormalizeEmail } from "../../utils/emailValidation";
import { validateAndNormalizePhone } from "../../utils/phoneValidation";
import { FormData, ValidationState, FormScreen } from "./types";
import { InitialFormStage } from "./stages/InitialFormStage";
import { VerificationStage } from "./stages/VerificationStage";
import { SuccessStage } from "./stages/SuccessStage";
import { requestOTP, verifyOTP } from "./networking/otpService";
import { generateUserMarketingProfile } from "./networking/profileService";
import { getMonitoringService } from "../../lib/monitoring";
import useMetadataStore from "@/stores/metaDataStore";
import { updateUserMarketingProfile } from "../../lib/networking/updateUserMarketingProfile";

interface GenericFormProps {
  /**
   * The main title shown at the top of the form
   * @default "Save Your Deal For Later"
   */
  title?: string;
  /**
   * The subtitle shown below the main title
   * @default "Please enter your information below to save your deal for later."
   */
  subtitle?: string;
  /**
   * The text shown on the submit button
   * @default "Submit"
   */
  submitButtonText?: string;
  /**
   * Whether the user has confirmed their identity with the marketing profile
   * @default false
   */
  confirmedUserProfile?: boolean;
  onClose?: () => void;
}

export const GenericForm = ({
  title = "Save Your Deal For Later",
  subtitle = "Please enter your information below to save your deal for later.",
  confirmedUserProfile = false,
  onClose = () => {},
}: GenericFormProps) => {
  const metadataStore = useMetadataStore();
  const [currentScreen, setCurrentScreen] = useState<FormScreen>("initial");
  const [formData, setFormData] = useState<FormData>({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    marketingConsent: true,
    verificationCode: "",
  });

  // Store the profile ID when created
  const [profileId, setProfileId] = useState<string | null>(null);

  // Track which contact method needs verification
  const [verificationMethod, setVerificationMethod] = useState<
    "email" | "phone" | null
  >(null);

  const [validation, setValidation] = useState<ValidationState>({
    email: {
      isValid: true,
      message: "",
      isDirty: false,
    },
    phone: {
      isValid: true,
      message: "",
      isDirty: false,
    },
    marketingConsent: {
      isValid: true,
      message: "",
    },
  });

  const [isLoading, setIsLoading] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<
    "idle" | "success" | "error"
  >("idle");

  const [error, setError] = useState<string | null>(null);

  // Validate email as user types
  const validateEmail = (email: string) => {
    if (!email) {
      return { isValid: true, message: "" }; // Empty is ok since it's optional
    }
    const { isValid, normalizedEmail } = validateAndNormalizeEmail(email);
    return {
      isValid,
      message: isValid ? "" : "Please enter a valid email address",
    };
  };

  const handleInputChange = (e: Event) => {
    const target = e.target as HTMLInputElement | HTMLSelectElement;
    const name = target.name;

    // Special handling for checkbox inputs
    if (target.type === "checkbox") {
      setFormData((prev) => ({
        ...prev,
        [name]: (target as HTMLInputElement).checked,
      }));
      return;
    }

    const value = target.value;

    // For phone numbers, store the normalized version if valid
    if (name === "phone") {
      const { isValid, normalizedPhone } = validateAndNormalizePhone(value);
      setFormData((prev) => ({
        ...prev,
        [name]: isValid ? normalizedPhone || value : value,
      }));
      setValidation((prev) => ({
        ...prev,
        phone: {
          isValid,
          message: isValid
            ? ""
            : "Please enter a valid phone number (country code + phone number, e.g. +1 for US/Canada followed by your 10-digit number)",
          isDirty: true,
        },
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Validate email in real-time
    if (name === "email") {
      const emailValidation = validateEmail(value);
      setValidation((prev) => ({
        ...prev,
        email: {
          ...emailValidation,
          isDirty: true,
        },
      }));
    }
  };

  const determineVerificationMethod = (
    formData: FormData
  ): "email" | "phone" => {
    // Prefer phone over email if both are provided
    if (formData.phone && validation.phone.isValid) {
      return "phone";
    } else if (formData.email && validation.email.isValid) {
      return "email";
    }
    // Default to email if neither is valid (shouldn't happen due to validation)
    return "email";
  };

  const handleInitialSubmit = async (e: Event) => {
    e.preventDefault();

    // Validate that at least one contact method is provided
    if (!formData.email && !formData.phone) {
      setError("Please provide either an email address or phone number.");
      return;
    }

    // If email is provided, validate it
    if (formData.email && !validation.email.isValid) {
      setError("Please enter a valid email address.");
      return;
    }

    // If phone is provided, validate it
    if (formData.phone && !validation.phone.isValid) {
      setError("Please enter a valid phone number.");
      return;
    }

    // Validate marketing consent checkbox
    if (!formData.marketingConsent) {
      setValidation((prev) => ({
        ...prev,
        marketingConsent: {
          isValid: false,
          message: "Please check this box to receive marketing updates.",
        },
      }));
      setError("Please check the marketing consent box to continue.");
      return;
    }

    setIsLoading(true);
    setSubmitStatus("idle");
    setError(null);

    try {
      let response;
      // Check if user confirmed their identity with a marketing profile
      if (
        confirmedUserProfile &&
        metadataStore.offerMetadata?.userMarketingProfile?.id
      ) {
        console.log("Updating confirmed user marketing profile");
        // We don't need to explicitly pass ID, updateUserMarketingProfile will get it from session storage
        response = await updateUserMarketingProfile(
          metadataStore.offerMetadata?.userMarketingProfile?.id,
          formData
        );
      } else {
        console.log("Creating new user marketing profile");
        response = await generateUserMarketingProfile(formData);
      }

      if (response.type === "ERROR") {
        setError(
          response.error?.errorMessage ||
            "Something went wrong. Please try again."
        );
        setSubmitStatus("error");
        return;
      }

      if (response.success) {
        // Store the profile ID for later use
        const newProfileId = response.success.id;
        setProfileId(newProfileId);

        // Use the Monitoring Service to set UMP ID and create UMPO with stored events
        const monitoringService = getMonitoringService();
        if (monitoringService) {
          try {
            // First set the User Marketing Profile ID
            monitoringService.setUserMarketingProfileId(newProfileId);

            // Then create the UMPO with all stored events
            monitoringService
              .createUMPOWithStoredEvents(newProfileId)
              .then(() => {
                console.log(
                  "Form: Successfully created UMPO with stored events"
                );
              })
              .catch((error) => {
                console.error(
                  "Form: Failed to create UMPO with stored events:",
                  error
                );
                // Continue with the form flow even if UMPO creation fails
              });
          } catch (error) {
            console.error("Form: Error in monitoring service setup:", error);
            // Continue with the form flow even if there's an error
          }
        }

        // Determine which contact method to use
        const method = determineVerificationMethod(formData);
        setVerificationMethod(method);

        try {
          await requestOTP(newProfileId, method);
          setCurrentScreen("verification");
        } catch (otpError) {
          console.error("Failed to request OTP:", otpError);
          setError("Failed to send verification code. Please try again.");
          setSubmitStatus("error");
        }
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setError("Something went wrong. Please try again.");
      setSubmitStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationSubmit = async (e: Event) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      if (!profileId) {
        throw new Error("No profile ID found in state");
      }

      if (!verificationMethod) {
        throw new Error("No verification method selected");
      }

      // Verify the OTP - this will also emit the offerSaved event when successful
      await verifyOTP(
        profileId,
        formData.verificationCode || "",
        verificationMethod
      );

      // After successful verification, move to success screen
      setCurrentScreen("success");
    } catch (error) {
      console.error("Error verifying code:", error);
      setError(
        error instanceof Error
          ? error.message
          : "Failed to verify code. Please try again."
      );
      setSubmitStatus("error");
    } finally {
      setIsLoading(false);
    }
  };

  const renderScreen = () => {
    switch (currentScreen) {
      case "initial":
        return (
          <InitialFormStage
            formData={formData}
            validation={validation}
            isLoading={isLoading}
            title={title}
            subtitle={subtitle}
            onSubmit={handleInitialSubmit}
            onInputChange={handleInputChange}
          />
        );

      case "verification":
        return (
          <VerificationStage
            formData={formData}
            isLoading={isLoading}
            verificationMethod={verificationMethod!}
            onSubmit={handleVerificationSubmit}
            onInputChange={handleInputChange}
          />
        );

      case "success":
        return <SuccessStage onClose={onClose} />;

      default:
        return (
          <InitialFormStage
            formData={formData}
            validation={validation}
            isLoading={isLoading}
            title={title}
            subtitle={subtitle}
            onSubmit={handleInitialSubmit}
            onInputChange={handleInputChange}
          />
        );
    }
  };

  return (
    <div className="kismet-form h-full flex flex-col">
      <div className="flex-1 overflow-y-auto p-6 md:p-8">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-600 p-4 rounded-lg text-center mb-6">
            {error}
          </div>
        )}
        {renderScreen()}
      </div>
    </div>
  );
};
